import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, Scan, Building2, TrendingUp, Shield, Box, Map, Eye, FileText, Layers, Landmark, PencilRuler } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { industries, localize } from '../mock';
import { useLanguage } from '../context/LanguageContext';
import translations from '../translations';
import SEO from '../components/SEO';
import { SectionHeading } from '../components/Sections';

const iconComponents = [Box, Map, Eye, FileText, Layers, Landmark];
const audienceIcons = [Building2, PencilRuler, TrendingUp];
const whyChooseIcons = [Building2, TrendingUp, Shield];

const PointCloudCard = ({ service, icon: Icon }) => (
  <div className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-6 hover:bg-white/10 transition-all duration-300 group">
    <div className="bg-orange-500/20 w-14 h-14 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-500/30 transition-colors">
      <Icon className="h-7 w-7 text-orange-400" />
    </div>
    <h3 className="text-xl font-bold mb-3 group-hover:text-orange-500 transition-colors">{service.title}</h3>
    <p className="text-gray-300 text-sm">{service.description}</p>
  </div>
);

const AudienceCard = ({ audience, icon: Icon }) => (
  <Card className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 group">
    <CardContent className="p-8">
      <div className="bg-gradient-to-br from-orange-500 to-orange-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
        <Icon className="h-8 w-8 text-white" />
      </div>
      <h3 className="text-2xl font-bold text-gray-900 mb-4">{audience.title}</h3>
      <p className="text-gray-600 mb-6">{audience.description}</p>
      <ul className="space-y-2">
        {audience.benefits.map((benefit) => (
          <li key={benefit} className="flex items-center space-x-2 text-sm text-gray-700">
            <CheckCircle className="h-4 w-4 text-orange-500 flex-shrink-0" />
            <span>{benefit}</span>
          </li>
        ))}
      </ul>
    </CardContent>
  </Card>
);

const WhyChooseItem = ({ item, icon: Icon }) => (
  <div className="flex items-start space-x-4">
    <div className="bg-orange-100 p-3 rounded-lg">
      <Icon className="h-6 w-6 text-orange-500" />
    </div>
    <div>
      <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
      <p className="text-gray-600">{item.description}</p>
    </div>
  </div>
);

const IndustryCard = ({ industry, lang, learnMoreLabel }) => (
  <Card className="group hover:shadow-2xl transition-all duration-500 border-0 overflow-hidden">
    <div className="relative h-64 overflow-hidden">
      <img src={industry.image} alt={localize(industry, 'name', lang)} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
      <div className="absolute inset-0 bg-gradient-to-t from-gray-900/90 via-gray-900/40 to-transparent"></div>
      <div className="absolute bottom-4 left-4 right-4">
        <h3 className="text-2xl font-bold text-white mb-2">{localize(industry, 'name', lang)}</h3>
      </div>
    </div>
    <CardContent className="p-6">
      <p className="text-gray-600 mb-4">{localize(industry, 'description', lang)}</p>
      <ul className="space-y-2">
        {(lang === 'es' ? industry.benefits_es : industry.benefits).slice(0, 3).map((benefit) => (
          <li key={benefit} className="flex items-start space-x-2 text-sm">
            <CheckCircle className="h-5 w-5 text-orange-500 flex-shrink-0 mt-0.5" />
            <span className="text-gray-700">{benefit}</span>
          </li>
        ))}
      </ul>
      <Link to="/industries">
        <Button variant="ghost" className="mt-4 text-orange-500 hover:text-orange-600 p-0 h-auto font-semibold group/btn">
          {learnMoreLabel}
          <ArrowRight className="ml-2 h-4 w-4 group-hover/btn:translate-x-1 transition-transform" />
        </Button>
      </Link>
    </CardContent>
  </Card>
);

const Home = () => {
  const { lang } = useLanguage();
  const t = translations[lang].home;

  return (
    <div className="min-h-screen">
      <SEO path="/" />

      {/* Hero Section */}
      <section data-testid="home-hero" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img src="https://images.unsplash.com/photo-1684450471771-b70596cf13dd" alt="BIM Technology" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-gray-900/85 to-gray-900/75"></div>
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32 text-center">
          <div className="space-y-8">
            <div className="inline-flex items-center space-x-2 bg-orange-500/10 backdrop-blur-sm border border-orange-500/20 rounded-full px-6 py-2 mb-4">
              <Scan className="h-4 w-4 text-orange-400" />
              <span className="text-orange-400 text-sm font-medium">{t.heroTag}</span>
            </div>
            <h1 className="text-5xl md:text-7xl font-bold text-white leading-tight">
              {t.heroTitle1}<br /><span className="text-orange-500">{t.heroTitle2}</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto leading-relaxed">{t.heroDescription}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center pt-4">
              <Link to="/contact">
                <Button data-testid="hero-request-consultation-btn" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-2xl hover:scale-105 group">
                  {t.requestConsultation}
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
              <Link to="/services">
                <Button data-testid="hero-explore-services-btn" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg rounded-lg transition-all duration-300">
                  {t.exploreServices}
                </Button>
              </Link>
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center">
            <div className="w-1 h-3 bg-orange-500 rounded-full mt-2"></div>
          </div>
        </div>
      </section>

      {/* Point Cloud Services */}
      <section data-testid="point-cloud-section" className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              {t.pointCloudTitle.split(' ').slice(0, 1).join(' ')} <span className="text-orange-500">{t.pointCloudTitle.split(' ').slice(1).join(' ')}</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">{t.pointCloudSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {t.pointCloudServices.map((service, index) => (
              <PointCloudCard key={service.title} service={service} icon={iconComponents[index]} />
            ))}
          </div>
          <div className="text-center mt-12">
            <Link to="/services">
              <Button data-testid="explore-all-services-btn" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-2xl hover:scale-105 group">
                {t.exploreAllServices}
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Industries */}
      <section data-testid="industries-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading title={t.industriesTitle} titleAccent={t.industriesTitleAccent} subtitle={t.industriesSubtitle} />
          <div className="grid md:grid-cols-2 gap-8">
            {industries.map((industry) => (
              <IndustryCard key={industry.id} industry={industry} lang={lang} learnMoreLabel={t.learnMore} />
            ))}
          </div>
        </div>
      </section>

      {/* 3D Rendering & Digital Twins */}
      <section data-testid="visualization-section" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t.vizTitle} <span className="text-orange-500">{t.vizTitleAccent}</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">{t.vizSubtitle}</p>
          </div>
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {t.audiences.map((audience, index) => (
              <AudienceCard key={audience.title} audience={audience} icon={audienceIcons[index]} />
            ))}
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-2xl p-8 md:p-12">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">{t.whatWeDeliver}</h3>
                <div className="space-y-4">
                  {t.deliverables.map((item) => (
                    <div key={item} className="flex items-start space-x-3">
                      <div className="bg-orange-500 rounded-full p-1 mt-1">
                        <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                      <span className="text-gray-700">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
              <div className="bg-white rounded-xl p-8 shadow-lg">
                <div className="text-center">
                  <div className="text-5xl font-bold text-orange-500 mb-2">{t.fromLabel}</div>
                  <div className="text-3xl font-bold text-gray-900 mb-4">{t.conceptToReality}</div>
                  <p className="text-gray-600 mb-6">{t.conceptDescription}</p>
                  <Link to="/services">
                    <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6 py-3 rounded-lg transition-all duration-300 hover:shadow-xl">
                      {t.exploreVisualization}
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section data-testid="why-choose-section" className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
                {t.whyChooseTitle} <span className="text-orange-500">{t.whyChooseAccent}</span>
              </h2>
              <p className="text-lg text-gray-600 mb-8">{t.whyChooseDescription}</p>
              <div className="space-y-6">
                {t.whyChooseItems.map((item, index) => (
                  <WhyChooseItem key={item.title} item={item} icon={whyChooseIcons[index]} />
                ))}
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1618385455730-2571c38966b7" alt="BIM Professional" className="rounded-2xl shadow-2xl" />
              <div className="absolute -bottom-6 -left-6 bg-orange-500 text-white p-6 rounded-xl shadow-xl">
                <div className="text-3xl font-bold">98%</div>
                <div className="text-sm">{t.clientSatisfaction}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section data-testid="home-cta" className="py-24 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">{t.ctaTitle}</h2>
          <p className="text-xl text-gray-300 mb-8">{t.ctaDescription}</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button data-testid="cta-request-consultation-btn" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg rounded-lg transition-all duration-300 hover:shadow-2xl hover:scale-105">
                {t.requestConsultation}
              </Button>
            </Link>
            <Link to="/services#service-portfolio">
              <Button data-testid="cta-view-services-btn" variant="outline" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 px-8 py-6 text-lg rounded-lg transition-all duration-300">
                {t.viewServices}
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
